import { SetStateAction, useCallback, useMemo, useState } from 'react';
import { RecordingAsset } from 'types';

import { useRecordingAssets, useRecordingSettings } from '@kaltura-ott/tvpil-shared';

import { RailProviderData, RailSettings } from 'components/widgets/Rail/components/RailProvider/types';
import { useHandleSkeletonRef } from 'components/widgets/Rail/hooks/useHandleSkeletonRef/useHandleSkeletonRef';
import { DEFAULT_IMAGE_PARAMS, RAIL_PAGE_SIZE } from 'consts';
import { useCheckDataIsEmpty } from 'hooks/common/useCheckDataIsEmpty/useCheckDataIsEmpty';
import { useRhinoErrorReaction } from 'hooks/common/useRhinoErrorReaction';

function useRecordingRail(referenceId: string) {
  const originalSettings = useRecordingSettings({ referenceId, ...DEFAULT_IMAGE_PARAMS });
  const originalData = useRecordingAssets({ referenceId, pageSize: RAIL_PAGE_SIZE });
  const isRailEmpty = useCheckDataIsEmpty(originalData);
  const isRailInvalid = isRailEmpty || Boolean(originalData.error);
  const isLoading = originalData.isLoading && !originalData.items.length;

  const [shouldPreventDisplay, setShouldPreventDisplay] = useState(true);

  const setShouldPreventDisplayForward = useCallback(
    (value: SetStateAction<boolean>) => setShouldPreventDisplay(value),
    [],
  );

  const items: Array<RecordingAsset> = useMemo(
    () =>
      originalData.items.map((recordingItem) => ({
        id: recordingItem.asset.id,
        images: recordingItem.asset.images,
        ...recordingItem,
      })),
    [originalData.items],
  );

  const preloadImages = useMemo(
    () =>
      originalData.items.map((recordingItem) => ({
        images: recordingItem.asset.images,
      })),
    [originalData.items],
  );

  const data: RailProviderData<RecordingAsset> = {
    items,
    totalCount: originalData.totalCount,
    hasNextPage: originalData.hasNextPage,
    isLoading: originalData.isLoading,
    error: originalData.error,
    fetchNextPage: originalData.fetchNextPage,
    refetch: originalData.refetch,
  };

  const settings: RailSettings = {
    branding: originalSettings?.branding,
    id: originalSettings?.id,
    imageHeight: originalSettings?.imageHeight,
    imageType: originalSettings?.imageType,
    imageWidth: originalSettings?.imageWidth,
    title: originalSettings?.title,
    moreLink: originalSettings?.moreLink,
    shouldShowRating: Boolean(originalSettings?.shouldShowRating),
    newEpisodeRetention: originalSettings?.newEpisodeRetention,
    recordingExpirationBoundary: originalSettings?.recordingExpirationBoundary
      ? Number(originalSettings?.recordingExpirationBoundary)
      : undefined,
    recordingPaddingEnd: originalSettings?.recordingPaddingEnd,
  };

  const handleSkeletonRef = useHandleSkeletonRef(setShouldPreventDisplayForward, preloadImages, settings);

  useRhinoErrorReaction({ error: originalData.error, refetch: originalData.refetch });

  return {
    data,
    settings: originalSettings ? settings : null,
    isLoading,
    isRailInvalid,
    handleSkeletonRef,
    shouldPreventDisplay,
  };
}

export { useRecordingRail };
